import { useState } from "react";
import { Link, Outlet } from "react-router-dom";

import type { Capabilities } from "../api/capabilities.js";
import { useAuth } from "../auth/AuthContext.js";
import { ForcedPasswordChange } from "../auth/ForcedPasswordChange.js";
import { SettingsModal } from "../desktop/SettingsModal.js";
import { useDesktopSettings } from "../desktop/useDesktopSettings.js";
import "./AppShell.css";

/**
 * The persistent frame around every authenticated route: a top bar plus the
 * routed page.
 *
 * When the signed-in account has `mustChangePassword` set, this renders
 * `ForcedPasswordChange` in place of `<Outlet/>` — see that component's doc
 * comment for why the gate lives here rather than as its own route.
 */
export function AppShell({ capabilities }: { capabilities: Capabilities }) {
  const auth = useAuth();
  const isHosted = capabilities.deployment === "hosted";
  const mustChangePassword = isHosted && auth.account?.mustChangePassword === true;

  // Desktop-only settings screen — the successor to legacy's Settings.xaml. The
  // bridge is null on hosted (and on a LAN browser), so the gear never shows there.
  const desktop = useDesktopSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <span className="app-title">Skladiščenje gum</span>
        <div className="app-topbar-right">
          {isHosted && (
            <>
              {!mustChangePassword && auth.account?.role === "admin" && (
                <Link to="/accounts" className="app-nav-link">
                  Uporabniki
                </Link>
              )}
              {auth.displayName && <span className="app-employee-name">{auth.displayName}</span>}
              <button type="button" className="app-logout" onClick={() => void auth.logout()}>
                Odjava
              </button>
            </>
          )}
          {desktop.bridge !== null && (
            <button type="button" className="app-settings-button" onClick={() => setSettingsOpen(true)} aria-label="Nastavitve">
              ⚙ Nastavitve
            </button>
          )}
        </div>
      </header>
      <div className="app-content">{mustChangePassword ? <ForcedPasswordChange /> : <Outlet />}</div>
      {settingsOpen && desktop.bridge !== null && desktop.settings !== undefined && (
        <SettingsModal bridge={desktop.bridge} settings={desktop.settings} persist={desktop.update} onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}
